import { addDays, format, subDays } from 'date-fns';
import pLimit from 'p-limit';
import { useSelector } from 'react-redux';
import useSWRImmutable from 'swr/immutable';
import { useUnitProvider } from '/@/core/context/unit';
import settings from '/@/core/settings';
import { axFetch } from '/@/store/session/actions';
import { getSessionCountry } from '/@/store/session/selectors';
import type { ForecastWeatherResponse } from '/@/types/weather/forecast';
import { formatDate } from '/@/utils';
export const axFetchGetter = (url) => axFetch.get(url).then((res) => res.data);

export const useFrostRiskMaps = () => {
    const country = useSelector(getSessionCountry);
    return useSWRImmutable(
        `${settings.urls.apiDomain}/core/v1/maps/frost-risk-map/?country__code=${country?.code}`,
        axFetchGetter
    );
};
export const useGDUsMaps = () => {
    const country = useSelector(getSessionCountry);
    return useSWRImmutable(
        `${settings.urls.apiDomain}/core/v1/maps/gdus-map/?country__code=${country?.code}`,
        axFetchGetter
    );
};

export const getWeatherForecast = async (lat: number, lng: number, system: string) => {
    const apiUrl = new URL(settings.urls.weather.getWeather);
    const params = new URLSearchParams();
    params.append('lat', lat.toString());
    params.append('lng', lng.toString());
    if (system) params.append('system', system);
    apiUrl.search = params.toString();

    const res = await axFetch.get<ForecastWeatherResponse>(apiUrl.toString());

    return res.data;
};

export const useWeatherForecast = ({ lat, lng }: { lat: number; lng: number }) => {
    const units = useUnitProvider();
    let key = null;
    if (lat && lng) key = { key: 'useWeatherForecast', lat, lng, system: units.system };
    return useSWRImmutable(key, (key) => getWeatherForecast(key.lat, key.lng, key.system));
};

export const getGduFrostRisk = async ({
    date,
    lat,
    lng,
    locationId,
    system,
}: {
    date: string;
    lat: number;
    lng: number;
    locationId: number;
    system: string;
}) => {
    const plantingDate = new Date(date.replace(/-/g, '/').replace(/T.+/, ''));
    const from = format(subDays(plantingDate, 8), 'yyyy/MM/dd');
    const to = format(addDays(plantingDate, 8), 'yyyy/MM/dd');
    const today = new Date();
    const sinceDate = new Date(from);
    const toDate = new Date(to);
    const passedDays = [];
    const commingDates = [];

    for (let d = new Date(sinceDate); d < today; d.setDate(d.getDate() + 1)) {
        passedDays.push(new Date(d));
    }
    for (let d = new Date(today); d <= toDate; d.setDate(d.getDate() + 1)) {
        if (d >= sinceDate) {
            commingDates.push(new Date(d));
        }
    }

    let forecast_historical = [];
    if (passedDays.length) {
        const fromPassedDays = passedDays[0].toISOString().split('T')[0];
        const toPassedDays = passedDays[passedDays.length - 1].toISOString().split('T')[0];

        const apiUrl = new URL(settings.urls.weather.getHistoricalWeather);
        const params = new URLSearchParams();

        params.append('lat', lat);
        params.append('lng', lng);
        params.append('from', fromPassedDays);
        params.append('to', toPassedDays);
        params.append('system', system);
        apiUrl.search = params.toString();
        const res = await axFetch.get(apiUrl.toString());
        forecast_historical = res.data.results
            .slice()
            .reverse()
            .map((dayForecast) => ({
                date: formatDate(new Date(dayForecast.date)),
                value: dayForecast.gdu,
            }));
    }
    let historical_data = [];
    if (commingDates.length && locationId) {
        const fromCommingDays = commingDates[0].toISOString().split('T')[0];
        const toCommingDays = commingDates[commingDates.length - 1].toISOString().split('T')[0];
        const apiUrl = new URL(
            `${settings.urls.apiDomain}/core/v1/zones/historic-gdus/?location=${locationId}&start_date=${fromCommingDays}&end_date=${toCommingDays}`,
            window.location.origin
        );
        const res = await axFetch.get(apiUrl.toString());
        historical_data = res.data.values.map((dayForecast) => ({
            date: formatDate(new Date(dayForecast.date)),
            value: dayForecast.value,
        }));
    }
    let forecast_data = [];

    const diffTime = new Date(plantingDate) - today;
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    if (Math.abs(diffDays) <= 15) {
        try {
            const forecast = await getWeatherForecast(lat, lng, system);
            forecast_data = forecast.data.forecast
                .filter((dayForecast) => new Date(dayForecast.date) <= toDate)
                .map((dayForecast) => ({
                    date: formatDate(new Date(dayForecast.date)),
                    value: dayForecast.data[0].day.gdu,
                }));
        } catch (er) {
            console.error(er);
        }
    }

    const allSources = [...forecast_historical, ...historical_data, ...forecast_data];
    const dateMap = new Map();

    allSources.forEach((entry) => {
        dateMap.set(entry.date, entry.value);
    });

    return Array.from(dateMap, ([date, value]) => ({ date, value }));
};

const limit = pLimit(5);

export const useFieldsWeatherForecast = (fields) => {
    const units = useUnitProvider();
    return useSWRImmutable([fields], async (fields) => {
        const locations = fields.reduce((locations, field) => {
            const { location } = field;

            const splittedLocation = location.split(',');
            const parsedLocation = splittedLocation.slice(0, splittedLocation.length - 2).join(',');

            if (!(location in locations)) locations[parsedLocation] = [];
            locations?.[parsedLocation]?.push(field);
            return locations;
        }, {});

        const locationsWeatherData = await Promise.all(
            Object.values(locations).map((locationFields) => {
                const { lat, lng } = locationFields.reduce(
                    (locationData, field) => {
                        locationData.lat += field.lat / locationFields.length;
                        locationData.lng += field.lng / locationFields.length;
                        return locationData;
                    },
                    { lat: 0, lng: 0 }
                );

                return limit(() => getWeatherForecast(lat, lng, units.system));
            })
        );

        return Object.keys(locations).map((location, index) => {
            return {
                location,
                forecast: locationsWeatherData?.[index]?.data.forecast,
            };
        });
    });
};

export const useFrostRiskGdus = ({ lat, lng, date, locationId }) => {
    let key = { locationId, date, lat, lng, key: 'useGDUData' };
    const units = useUnitProvider();
    if (!date || !lat || !lng) key = null;
    return useSWRImmutable(key, ({ lat, lng, date, locationId }) =>
        getGduFrostRisk({ lat, lng, date, locationId, system: units.system })
    );
};
